// ActivityLog — a full per-step Smart Links activity log. Every raw event
// (clicked / installed / first-open / opened) with time, step, platform, device/
// OS, location, referrer and flags. Client-side step filter + CSV export. Reused
// by the link detail page (per-link) and the global Activity feed (showLink).
import React, { useMemo, useState } from 'react';
import {
  Box, Heading, HStack, Text, Badge, Button, Select, Table, Thead, Tbody, Tr, Th, Td,
  TableContainer, Tag, Tooltip, Flex,
} from '@chakra-ui/react';
import { FiDownload } from 'react-icons/fi';
import type { AnalyticsEvent } from '../../types/link';
import { formatRelative } from '../../lib/format';

const EVENT_SCHEME: Record<AnalyticsEvent['eventName'], string> = {
  link_clicked: 'blue',
  app_installed: 'green',
  app_first_open: 'teal',
  app_opened: 'purple',
};
const EVENT_LABEL: Record<AnalyticsEvent['eventName'], string> = {
  link_clicked: 'Clicked',
  app_installed: 'Installed',
  app_first_open: 'First open',
  app_opened: 'Opened',
};

const fullTime = (at: string) => {
  try { return new Date(at).toLocaleString(); } catch { return at; }
};
const loc = (e: AnalyticsEvent) => [e.city, e.region, e.country].filter(Boolean).join(', ');
const csvCell = (v: unknown) => {
  const s = v === null || v === undefined ? '' : String(v);
  return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
};

export const ActivityLog: React.FC<{ events: AnalyticsEvent[]; title?: string; showLink?: boolean }> = ({
  events, title = 'Activity log', showLink = false,
}) => {
  const [step, setStep] = useState<'all' | AnalyticsEvent['eventName']>('all');

  const rows = useMemo(
    () => (step === 'all' ? events : events.filter((e) => e.eventName === step)),
    [events, step],
  );

  const exportCsv = () => {
    const cols: Array<[string, (e: AnalyticsEvent) => unknown]> = [
      ['time', (e) => e.at],
      ['step', (e) => e.eventName],
      ...(showLink ? [['link_id', (e: AnalyticsEvent) => e.linkId] as [string, (e: AnalyticsEvent) => unknown]] : []),
      ['platform', (e) => e.platform],
      ['os', (e) => e.os],
      ['device', (e) => e.device],
      ['country', (e) => e.country],
      ['region', (e) => e.region],
      ['city', (e) => e.city],
      ['channel', (e) => e.channel],
      ['referrer', (e) => e.referrer],
      ['in_app_browser', (e) => (e.inAppBrowser ? 'yes' : '')],
      ['is_bot', (e) => (e.isBot ? 'yes' : '')],
      ['click_id', (e) => e.id],
    ];
    const header = cols.map(([h]) => h).join(',');
    const body = rows.map((e) => cols.map(([, get]) => csvCell(get(e))).join(',')).join('\n');
    const blob = new Blob(['﻿' + header + '\n' + body], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `smartlink-activity_${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(a); a.click(); document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  return (
    <Box bg="white" borderWidth="1px" rounded="lg" p={4} shadow="xs">
      <Flex justify="space-between" align="center" mb={3} gap={2} wrap="wrap">
        <Heading size="xs" color="gray.600">{title}</Heading>
        <HStack spacing={2}>
          <Select size="xs" width="150px" value={step} onChange={(e) => setStep(e.target.value as typeof step)}>
            <option value="all">All steps</option>
            <option value="link_clicked">Clicked</option>
            <option value="app_installed">Installed</option>
            <option value="app_first_open">First open</option>
            <option value="app_opened">Opened</option>
          </Select>
          <Button size="xs" variant="outline" leftIcon={<FiDownload />} onClick={exportCsv} isDisabled={rows.length === 0}>
            CSV
          </Button>
        </HStack>
      </Flex>

      {rows.length === 0 ? (
        <Text color="gray.400" fontSize="sm" py={4}>No events yet.</Text>
      ) : (
        <TableContainer maxH="520px" overflowY="auto">
          <Table size="sm" variant="simple">
            <Thead position="sticky" top={0} bg="gray.50" zIndex={1}>
              <Tr>
                <Th>Time</Th>
                <Th>Step</Th>
                {showLink && <Th>Link</Th>}
                <Th>Platform</Th>
                <Th>Device / OS</Th>
                <Th>Location</Th>
                <Th>Referrer</Th>
                <Th />
              </Tr>
            </Thead>
            <Tbody>
              {rows.map((e) => (
                <Tr key={`${e.id}-${e.eventName}-${e.at}`} _hover={{ bg: 'gray.50' }}>
                  <Td whiteSpace="nowrap">
                    <Tooltip label={fullTime(e.at)} openDelay={300}>
                      <Text fontSize="xs" color="gray.500">{formatRelative(e.at)}</Text>
                    </Tooltip>
                  </Td>
                  <Td>
                    <Badge colorScheme={EVENT_SCHEME[e.eventName]} variant="subtle" fontSize="0.65rem">
                      {EVENT_LABEL[e.eventName] ?? e.eventName}
                    </Badge>
                  </Td>
                  {showLink && <Td><Text fontSize="xs" color="gray.600" maxW="120px" isTruncated title={e.linkId}>{e.linkId || '—'}</Text></Td>}
                  <Td textTransform="capitalize" fontSize="sm" color="gray.700">{e.platform || '—'}</Td>
                  <Td fontSize="xs" color="gray.600">{[e.device, e.os].filter(Boolean).join(' · ') || '—'}</Td>
                  <Td fontSize="xs" color="gray.500">{loc(e) || '—'}</Td>
                  <Td fontSize="xs" color="gray.500" maxW="160px" isTruncated title={e.referrer}>{e.referrer || '—'}</Td>
                  <Td>
                    <HStack spacing={1}>
                      {e.inAppBrowser && <Tag size="sm" colorScheme="orange" variant="subtle" fontSize="0.6rem">in-app</Tag>}
                      {e.isBot && <Tag size="sm" colorScheme="red" variant="subtle" fontSize="0.6rem">bot</Tag>}
                    </HStack>
                  </Td>
                </Tr>
              ))}
            </Tbody>
          </Table>
        </TableContainer>
      )}
      <Text fontSize="xs" color="gray.400" mt={2}>Showing {rows.length} of {events.length} events (most recent first).</Text>
    </Box>
  );
};
